import Phaser from "phaser";
import { GAME } from "../config";
import { getMpClient } from "../net/MultiplayerClient";
import { getStoredCredentials } from "../net/playerIdentity";

type Platform = "ios" | "android" | "desktop" | "standalone";

function detectPlatform(): Platform {
  // Уже установлен как PWA?
  const standalone =
    window.matchMedia?.("(display-mode: standalone)").matches ||
    (navigator as any).standalone === true;
  if (standalone) return "standalone";

  const ua = navigator.userAgent;
  const isIOS = /iPhone|iPad|iPod/i.test(ua) ||
                (ua.includes("Mac") && (navigator.maxTouchPoints || 0) > 1);
  if (isIOS) return "ios";

  const isAndroid = /Android/i.test(ua);
  if (isAndroid) return "android";

  return "desktop";
}

/**
 * Точка входа. Решает, куда отправить пользователя:
 *   standalone (PWA уже установлено)  → MainMenu (или auto-login)
 *   есть креды в localStorage         → пытаемся auto-login → MainMenu
 *   иначе                             → инструкция установки + кнопка пропустить
 *
 * На Android умеет вызвать native install prompt (если событие пришло из index.html).
 */
export class InstallScene extends Phaser.Scene {
  constructor() { super("Install"); }

  create() {
    const platform = detectPlatform();
    const haveCreds = !!getStoredCredentials();

    // standalone или гость, но уже логинился раньше → пропускаем
    if (platform === "standalone" || haveCreds || platform === "desktop") {
      this.scene.start("MainMenu");
      return;
    }

    if (platform === "ios")     this.renderIos();
    else if (platform === "android") this.renderAndroid();
    else this.scene.start("MainMenu");
  }

  private renderIos() {
    const { WIDTH, HEIGHT } = GAME;
    this.bg();

    this.add.text(WIDTH / 2, 50, "ДОБАВЬ В ПРИЛОЖЕНИЯ", {
      fontFamily: "Arial Black", fontSize: "26px",
      color: "#ffd700", stroke: "#c41e3a", strokeThickness: 4,
    }).setOrigin(0.5);

    this.add.text(WIDTH / 2, 90, "Чтобы играть как в обычном приложении (без браузерных панелей):", {
      fontFamily: "Arial Black", fontSize: "13px", color: "#aaaaaa",
    }).setOrigin(0.5);

    const steps = [
      "1.   В Safari внизу — кнопка  📤  «Поделиться»",
      "2.   В меню — «На экран Домой»",
      "3.   Подтверди → иконка Banzai на главном экране",
      "4.   Открывай оттуда — игра запустится в полный экран",
    ];
    steps.forEach((line, i) => {
      this.add.text(WIDTH / 2, 150 + i * 38, line, {
        fontFamily: "Arial Black", fontSize: "16px", color: "#ffffff",
        stroke: "#000", strokeThickness: 3,
      }).setOrigin(0.5);
    });

    this.skipButton();
  }

  private renderAndroid() {
    const { WIDTH, HEIGHT } = GAME;
    this.bg();
    this.add.text(WIDTH / 2, 50, "ДОБАВЬ В ПРИЛОЖЕНИЯ", {
      fontFamily: "Arial Black", fontSize: "26px",
      color: "#ffd700", stroke: "#c41e3a", strokeThickness: 4,
    }).setOrigin(0.5);

    this.add.text(WIDTH / 2, 90, "Установи как обычное приложение — ярлык на рабочем столе, без URL-бара:", {
      fontFamily: "Arial Black", fontSize: "13px", color: "#aaaaaa", align: "center",
      wordWrap: { width: WIDTH - 80 },
    }).setOrigin(0.5);

    const promptEvent = (window as any).__bfInstallPrompt;
    if (promptEvent) {
      // нативный диалог Chrome
      const btn = this.add.rectangle(WIDTH / 2, HEIGHT / 2 - 20, 360, 70, 0x2a6a2a)
        .setStrokeStyle(4, 0xffd700)
        .setInteractive({ useHandCursor: true });
      this.add.text(WIDTH / 2, HEIGHT / 2 - 20, "✓ УСТАНОВИТЬ ПРИЛОЖЕНИЕ", {
        fontFamily: "Arial Black", fontSize: "20px",
        color: "#ffffff", stroke: "#000", strokeThickness: 3,
      }).setOrigin(0.5);
      const note = this.add.text(WIDTH / 2, HEIGHT / 2 + 30, "(откроется системный диалог)", {
        fontFamily: "Arial", fontSize: "12px", color: "#888888",
      }).setOrigin(0.5);
      btn.on("pointerdown", async () => {
        try {
          await promptEvent.prompt();
          const choice = await promptEvent.userChoice;
          (window as any).__bfInstallPrompt = null;
          if (choice && choice.outcome === "accepted") {
            note.setText("Устанавливается... вернёмся в игру");
            this.time.delayedCall(800, () => this.scene.start("MainMenu"));
          } else {
            note.setText("Можешь установить позже из меню браузера ⋮");
          }
        } catch (_e) { /* ignore */ }
      });
    } else {
      // нативного события нет (Firefox / Yandex / уже отказался) — текстовая инструкция
      const lines = [
        "1.   В Chrome нажми   ⋮   (меню справа сверху)",
        "2.   Выбери  «Установить приложение»  /  «На экран»",
        "3.   Подтверди — иконка появится на рабочем столе",
      ];
      lines.forEach((t, i) => {
        this.add.text(WIDTH / 2, 160 + i * 38, t, {
          fontFamily: "Arial Black", fontSize: "16px", color: "#ffffff",
          stroke: "#000", strokeThickness: 3,
        }).setOrigin(0.5);
      });
    }

    this.skipButton();
  }

  private skipButton() {
    const { WIDTH, HEIGHT } = GAME;
    const btn = this.add.rectangle(WIDTH / 2, HEIGHT - 50, 280, 44, 0x444444)
      .setStrokeStyle(2, 0xaaaaaa)
      .setInteractive({ useHandCursor: true });
    this.add.text(WIDTH / 2, HEIGHT - 50, "ПРОПУСТИТЬ — ИГРАТЬ В БРАУЗЕРЕ", {
      fontFamily: "Arial Black", fontSize: "14px", color: "#ffffff",
    }).setOrigin(0.5);
    btn.on("pointerdown", () => this.scene.start("MainMenu"));
  }

  private bg() {
    const { WIDTH, HEIGHT } = GAME;
    const g = this.add.graphics();
    g.fillGradientStyle(0x1a0808, 0x000000, 0x080000, 0x000000, 1);
    g.fillRect(0, 0, WIDTH, HEIGHT);

    // лого слева в углу
    if (this.textures.exists("logo")) {
      const logo = this.add.image(80, HEIGHT - 50, "logo").setOrigin(0.5).setAlpha(0.4);
      const sc = Math.min(120 / logo.width, 60 / logo.height);
      logo.setScale(sc);
    }
  }

  preload() {
    if (!this.textures.exists("logo")) {
      this.load.svg("logo", "assets/logo.svg", { width: 520, height: 220 });
    }
  }
}
